import { z } from 'zod';

/**
 * A fleet machine's lifecycle (ADR 0047, IMM-910.1). `pending` is the only entry
 * state; every other transition is an operator decision. Re-enrolment never
 * launders a `rejected` / `revoked` machine back to `pending`.
 */
export const FLEET_MACHINE_APPROVALS = ['pending', 'approved', 'rejected', 'revoked'] as const;
export const FleetMachineApprovalSchema = z.enum(FLEET_MACHINE_APPROVALS);
export type FleetMachineApproval = (typeof FLEET_MACHINE_APPROVALS)[number];

export const EnrolFleetMachineBodySchema = z.object({
  publicKey: z.string().regex(/^[0-9a-f]{64}$/),
  name: z.string().min(1).max(120),
  hostname: z.string().min(1).max(255),
  platform: z.string().min(1).max(64),
  agentVersion: z.string().min(1).max(32),
});

export type EnrolFleetMachineBody = z.infer<typeof EnrolFleetMachineBodySchema>;

export const EnrolFleetMachineResponseSchema = z.object({
  machineId: z.uuid(),
  approval: FleetMachineApprovalSchema,
});

export type EnrolFleetMachineResponse = z.infer<typeof EnrolFleetMachineResponseSchema>;

/** The inclusive range an operator may set a host's weekly limit within. */
export const FLEET_WEEKLY_LIMIT_PERCENT = { min: 1, max: 100 } as const;

/**
 * The operator-facing view of a machine. Never carries a token field — the
 * credential is minted lazily on the machine's own poll and is never returned
 * to an operator (ADR 0047).
 */
export const FleetMachineViewSchema = z.object({
  id: z.uuid(),
  publicKey: z.string(),
  name: z.string(),
  hostname: z.string(),
  platform: z.string(),
  agentVersion: z.string(),
  approval: FleetMachineApprovalSchema,
  /** The host's checked-out HEAD as last reported (`<short sha> <subject>`); null until a status event says. */
  checkoutCommit: z.string().nullable(),
  approvedByName: z.string().nullable(),
  approvedAt: z.iso.datetime().nullable(),
  firstSeenAt: z.iso.datetime(),
  lastSeenAt: z.iso.datetime(),
  /** The operator's weekly cap on this host, 1–100; `null` is no limit. */
  weeklyLimitPercent: z
    .number()
    .int()
    .min(FLEET_WEEKLY_LIMIT_PERCENT.min)
    .max(FLEET_WEEKLY_LIMIT_PERCENT.max)
    .nullable(),
});

export type FleetMachineView = z.infer<typeof FleetMachineViewSchema>;

export const ListFleetMachinesResponseSchema = z.object({ data: z.array(FleetMachineViewSchema) });

export type ListFleetMachinesResponse = z.infer<typeof ListFleetMachinesResponseSchema>;

/**
 * The machine's own view of itself, polled with a signature over the request.
 * `machineToken` is present exactly once — on the first poll after approval —
 * and is null on every later poll. No operator ever sees it.
 */
export const FleetMachineSelfResponseSchema = z.object({
  machineId: z.uuid(),
  approval: FleetMachineApprovalSchema,
  machineToken: z.string().min(32).nullable(),
});

export type FleetMachineSelfResponse = z.infer<typeof FleetMachineSelfResponseSchema>;

/** The key is required and `null` clears it: a missing key is a 400, never a clear. */
export const SetFleetMachineWeeklyLimitBodySchema = z.object({
  weeklyLimitPercent: z
    .number()
    .int()
    .min(FLEET_WEEKLY_LIMIT_PERCENT.min)
    .max(FLEET_WEEKLY_LIMIT_PERCENT.max)
    .nullable(),
});

export type SetFleetMachineWeeklyLimitBody = z.infer<typeof SetFleetMachineWeeklyLimitBodySchema>;

export const FleetMachinePathParamsSchema = z.object({ machineId: z.uuid() });
